import api from '../httpClient';
import type {
  OrganizationAdminOverviewData,
  OrganizationAdminResponseQuestion,
  OrganizationAdminResponseReportData,
  OrganizationAdminUserDetailData,
} from '../../types';

interface ResponseQuestionFilters {
  search?: string;
  courseId?: number;
  moduleId?: number;
}

export interface OrganizationAdminScope {
  organizationId?: string | null;
}

const buildParams = (...sources: Array<Record<string, string | number> | undefined>) => {
  const params = Object.assign({}, ...sources.filter(Boolean));
  return Object.keys(params).length ? params : undefined;
};

const buildScopeParams = (scope?: OrganizationAdminScope) => (
  scope?.organizationId ? { organization_id: scope.organizationId } : undefined
);

export const organizationAdmin = {
  getOverview: (scope?: OrganizationAdminScope) =>
    api.get<OrganizationAdminOverviewData>('/api/organization-admin/overview/', {
      params: buildScopeParams(scope),
    }).then((response) => response.data),
  getUserDetail: (userId: number, scope?: OrganizationAdminScope) =>
    api
      .get<OrganizationAdminUserDetailData>(`/api/organization-admin/users/${userId}/detail/`, {
        params: buildScopeParams(scope),
      })
      .then((response) => response.data),
  exportAggregateProgress: (scope?: OrganizationAdminScope) =>
    api.get('/api/organization-admin/exports/aggregate-progress/', {
      responseType: 'blob',
      params: buildScopeParams(scope),
    }),
  exportCombined: (userId?: number, scope?: OrganizationAdminScope) =>
    api.get('/api/organization-admin/exports/combined/', {
      responseType: 'blob',
      params: buildParams(
        buildScopeParams(scope),
        userId ? { user_id: userId } : undefined,
      ),
    }),
  exportUserProgress: (search?: string, scope?: OrganizationAdminScope) =>
    api.get('/api/organization-admin/exports/user-progress/', {
      responseType: 'blob',
      params: buildParams(
        buildScopeParams(scope),
        search ? { search } : undefined,
      ),
    }),
  exportUserDailyScores: (userId: number, scope?: OrganizationAdminScope) =>
    api.get(`/api/organization-admin/exports/users/${userId}/daily-scores/`, {
      responseType: 'blob',
      params: buildScopeParams(scope),
    }),
  exportUserDetailProgress: (userId: number, scope?: OrganizationAdminScope) =>
    api.get(`/api/organization-admin/exports/users/${userId}/detail-progress/`, {
      responseType: 'blob',
      params: buildScopeParams(scope),
    }),
  getResponseQuestions: (filters: ResponseQuestionFilters = {}, scope?: OrganizationAdminScope) =>
    api
      .get<{ results: OrganizationAdminResponseQuestion[] }>('/api/organization-admin/reports/response-questions/', {
        params: buildParams(
          buildScopeParams(scope),
          filters.search ? { search: filters.search } : undefined,
          filters.courseId ? { course_id: filters.courseId } : undefined,
          filters.moduleId ? { module_id: filters.moduleId } : undefined,
        ),
      })
      .then((response) => response.data.results),
  getResponseReport: (payload: { question_ids: number[]; user_ids?: number[] }, scope?: OrganizationAdminScope) =>
    api
      .post<OrganizationAdminResponseReportData>('/api/organization-admin/reports/response-data/', payload, {
        params: buildScopeParams(scope),
      })
      .then((response) => response.data),
  exportResponseUsers: (payload: { question_ids: number[]; user_ids?: number[] }, scope?: OrganizationAdminScope) =>
    api.post('/api/organization-admin/reports/response-data/export/users/', payload, {
      responseType: 'blob',
      params: buildScopeParams(scope),
    }),
  exportResponseAggregate: (payload: { question_ids: number[] }, scope?: OrganizationAdminScope) =>
    api.post('/api/organization-admin/reports/response-data/export/aggregate/', payload, {
      responseType: 'blob',
      params: buildScopeParams(scope),
    }),
};
